import { useEffect, useRef, useState, useCallback } from "react";
import { api, authedUrl, bytesToHuman, fmtErr, API, getToken } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../i18n";
import {
    UploadSimple,
    Trash,
    DownloadSimple,
    Image as ImageIcon,
    FilmStrip,
    Plus,
    X,
    Play,
    ShareNetwork,
    CheckCircle,
    Circle,
    Check,
    ListChecks,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import ShareModal from "../components/ShareModal";

export default function Gallery() {
    const { user } = useAuth();
    const { t } = useT();
    const [tab, setTab] = useState("photo");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploads, setUploads] = useState([]);
    const [preview, setPreview] = useState(null);
    const [sharing, setSharing] = useState(null);
    const [selectMode, setSelectMode] = useState(false);
    const [selected, setSelected] = useState(() => new Set());
    const [bulkBusy, setBulkBusy] = useState(false);
    const fileRef = useRef(null);

    const kind = tab === "photo" ? "image" : "video";
    // share/download/delete/bulk-ZIP — disponibile pentru toți utilizatorii
    const canDownload = true;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/media?kind=${kind}`);
            setItems(data);
        } catch (e) {
            toast.error(fmtErr(e.response?.data?.detail) || "Eroare la încărcare");
        } finally {
            setLoading(false);
        }
    }, [kind]);

    useEffect(() => {
        load();
        setSelected(new Set());
        setSelectMode(false);
    }, [load]);

    // Prevent tab close while uploads in progress
    useEffect(() => {
        const active = uploads.filter((u) => !u.error && (u.progress || 0) < 100);
        if (active.length === 0) return;
        const handler = (e) => { e.preventDefault(); e.returnValue = ""; return ""; };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [uploads]);

    const onPickFiles = (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = "";
        if (files.length === 0) return;
        if (files.length > 1) toast.message(`${files.length} fișiere — se încarcă în paralel`);
        files.forEach(uploadOne);
    };

    const uploadOne = async (file) => {
        const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        setUploads((u) => [...u, { id, name: file.name, progress: 0 }]);
        try {
            if ("Notification" in window && Notification.permission === "default") {
                Notification.requestPermission();
            }
        } catch (_) { /* noop */ }
        let wakeLock = null;
        const acquireLock = async () => {
            try {
                if ("wakeLock" in navigator && document.visibilityState === "visible") {
                    wakeLock = await navigator.wakeLock.request("screen");
                }
            } catch (_) { /* noop */ }
        };
        await acquireLock();
        const visHandler = () => { if (document.visibilityState === "visible") acquireLock(); };
        document.addEventListener("visibilitychange", visHandler);
        const form = new FormData();
        form.append("file", file);
        try {
            await api.post("/media/upload", form, {
                onUploadProgress: (ev) => {
                    if (!ev.total) return;
                    const p = Math.round((ev.loaded / ev.total) * 100);
                    setUploads((u) => u.map((x) => (x.id === id ? { ...x, progress: p } : x)));
                },
                timeout: 0,
                maxContentLength: Infinity,
                maxBodyLength: Infinity,
            });
            setUploads((u) => u.filter((x) => x.id !== id));
            toast.success(`Încărcat: ${file.name}`);
            try {
                if ("Notification" in window && Notification.permission === "granted") {
                    new Notification("db.mae", { body: `Încărcat: ${file.name}`, icon: "/icon-192.png", tag: "upload-done" });
                }
            } catch (_) { /* noop */ }
            load();
        } catch (e) {
            setUploads((u) =>
                u.map((x) => (x.id === id ? { ...x, error: fmtErr(e.response?.data?.detail) || e.message } : x))
            );
            toast.error(`Eșec: ${file.name}`);
            try {
                if ("Notification" in window && Notification.permission === "granted") {
                    new Notification("db.mae — eșec upload", { body: file.name, icon: "/icon-192.png", tag: "upload-fail" });
                }
            } catch (_) { /* noop */ }
        } finally {
            document.removeEventListener("visibilitychange", visHandler);
            if (wakeLock) { try { await wakeLock.release(); } catch (_) { /* noop */ } }
        }
    };

    const removeItem = async (item) => {
        if (!window.confirm(`${t("gal_delete_confirm")} "${item.original_name}"?`)) return;
        try {
            await api.delete(`/media/${item.id}`);
            setItems((x) => x.filter((i) => i.id !== item.id));
            toast.success(t("gal_deleted"));
            if (preview?.id === item.id) setPreview(null);
        } catch (e) {
            toast.error(fmtErr(e.response?.data?.detail) || "Error");
        }
    };

    const downloadItem = (item) => {
        const a = document.createElement("a");
        a.href = authedUrl(`/media/${item.id}/download`);
        a.download = item.original_name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const toggleSelect = (item) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(item.id)) next.delete(item.id);
            else next.add(item.id);
            return next;
        });
    };

    const selectAll = () => {
        if (selected.size === items.length) setSelected(new Set());
        else setSelected(new Set(items.map((i) => i.id)));
    };

    const exitSelect = () => {
        setSelectMode(false);
        setSelected(new Set());
    };

    const bulkDelete = async () => {
        if (selected.size === 0) return;
        if (!window.confirm(t("bulk_delete_confirm", { n: selected.size }))) return;
        setBulkBusy(true);
        try {
            const ids = Array.from(selected);
            const { data } = await api.post("/media/bulk_delete", { ids });
            setItems((arr) => arr.filter((i) => !selected.has(i.id)));
            toast.success(t("bulk_deleted", { n: data.deleted }));
            exitSelect();
        } catch (e) {
            toast.error(fmtErr(e.response?.data?.detail) || "Error");
        } finally {
            setBulkBusy(false);
        }
    };

    const bulkDownload = async () => {
        if (selected.size === 0) return;
        setBulkBusy(true);
        try {
            const ids = Array.from(selected);
            const token = getToken();
            const res = await fetch(`${API}/media/bulk_download`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                credentials: "include",
                body: JSON.stringify({ ids }),
            });
            if (!res.ok) {
                const t = await res.text();
                throw new Error(t || `HTTP ${res.status}`);
            }
            const blob = await res.blob();
            const cd = res.headers.get("content-disposition") || "";
            const m = cd.match(/filename="([^"]+)"/);
            const fname = m ? m[1] : `db-mae-${Date.now()}.zip`;
            const objUrl = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = objUrl;
            a.download = fname;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(objUrl);
            toast.success(`Arhivă descărcată (${selected.size} fișiere)`);
            exitSelect();
        } catch (e) {
            toast.error(e.message || "Eroare la descărcare");
        } finally {
            setBulkBusy(false);
        }
    };

    return (
        <div className="relative">
            <div className="flex items-end justify-between mb-6 gap-3">
                <div className="min-w-0">
                    <div className="label-mono mb-2">{t("gal_pre")}</div>
                    <h1 className="heading-tight fluid-h1">{t("gal_h1")}</h1>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    {items.length > 0 && !selectMode && (
                        <button
                            onClick={() => setSelectMode(true)}
                            data-testid="btn-select-mode"
                            className="btn-press h-11 px-3 flex items-center gap-2 border text-sm"
                            style={{ borderColor: "var(--border-strong)", color: "var(--text)" }}
                        >
                            <ListChecks size={18} />
                            <span className="hidden sm:inline">{t("gal_select")}</span>
                        </button>
                    )}
                    <button
                        onClick={() => fileRef.current?.click()}
                        data-testid="btn-upload"
                        className="btn-press flex items-center gap-2 px-4 h-11"
                        style={{ background: "var(--text)", color: "var(--bg)" }}
                    >
                        <UploadSimple size={18} />
                        <span className="text-sm font-medium">{t("gal_upload")}</span>
                    </button>
                </div>
                <input
                    ref={fileRef}
                    type="file"
                    multiple
                    accept={tab === "photo" ? "image/*" : "video/*"}
                    onChange={onPickFiles}
                    className="hidden"
                    data-testid="input-file"
                />
            </div>

            <div className="flex gap-1 mb-6 border-b" style={{ borderColor: "var(--border)" }}>
                <TabBtn active={tab === "photo"} onClick={() => setTab("photo")} icon={ImageIcon} label="Poze" testid="tab-photos" />
                <TabBtn active={tab === "video"} onClick={() => setTab("video")} icon={FilmStrip} label="Video" testid="tab-videos" />
            </div>

            {uploads.length > 0 && (
                <div className="mb-6 border p-4 space-y-2" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
                    <div className="label-mono">Se încarcă ({uploads.length})</div>
                    {uploads.map((u) => (
                        <div key={u.id} className="text-sm" data-testid={`upload-row-${u.id}`}>
                            <div className="flex justify-between mb-1">
                                <span className="truncate pr-3" style={{ color: "var(--text)" }}>{u.name}</span>
                                <span className="font-mono" style={{ color: u.error ? "var(--danger)" : "var(--text-muted)" }}>
                                    {u.error ? t("gal_error") : `${u.progress}%`}
                                </span>
                            </div>
                            <div className="h-0.5 w-full" style={{ background: "var(--surface-2)" }}>
                                <div className="h-0.5" style={{ width: `${u.progress}%`, background: u.error ? "var(--danger)" : "var(--text)" }} />
                            </div>
                            {u.error && <div className="text-xs mt-1" style={{ color: "var(--danger)" }}>{u.error}</div>}
                        </div>
                    ))}
                </div>
            )}

            {loading ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                    {Array.from({ length: 8 }).map((_, i) => (
                        <div key={i} className="aspect-square tile-shimmer" />
                    ))}
                </div>
            ) : items.length === 0 ? (
                <EmptyState
                    onUpload={() => fileRef.current?.click()}
                    label={tab === "photo" ? t("gal_empty_photos") : t("gal_empty_videos")}
                    btnLabel={t("gal_empty_btn")}
                />
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 pb-24">
                    {items.map((it) => (
                        <MediaTile
                            key={it.id}
                            item={it}
                            selectMode={selectMode}
                            isSelected={selected.has(it.id)}
                            onClick={() => (selectMode ? toggleSelect(it) : setPreview(it))}
                            onLongPress={() => {
                                if (!selectMode) {
                                    setSelectMode(true);
                                    toggleSelect(it);
                                }
                            }}
                            onDelete={() => removeItem(it)}
                            onDownload={canDownload ? () => downloadItem(it) : null}
                            onShare={canDownload ? () => setSharing(it) : null}
                        />
                    ))}
                </div>
            )}

            {selectMode && (
                <div className="fixed bottom-16 sm:bottom-6 left-0 right-0 z-40 px-3 sm:px-6 pointer-events-none">
                    <div
                        className="pointer-events-auto max-w-3xl mx-auto border p-3 flex items-center justify-between gap-3"
                        style={{ borderColor: "var(--border-strong)", background: "var(--surface)", boxShadow: "0 -20px 40px rgba(0,0,0,0.6)" }}
                        data-testid="bulk-bar"
                    >
                        <div className="flex items-center gap-2 min-w-0">
                            <button
                                onClick={selectAll}
                                data-testid="btn-select-all"
                                className="btn-press h-10 px-3 flex items-center gap-2 border text-sm"
                                style={{ borderColor: "var(--border)", color: "var(--text)" }}
                            >
                                <Check size={14} />
                                <span>{selected.size === items.length ? t("bulk_deselect") : t("bulk_all")}</span>
                            </button>
                            <div className="label-mono">{selected.size} / {items.length}</div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            {canDownload && (
                                <button
                                    onClick={bulkDownload}
                                    disabled={selected.size === 0 || bulkBusy}
                                    data-testid="btn-bulk-download"
                                    className="btn-press h-10 px-3 flex items-center gap-2 text-sm disabled:opacity-40"
                                    style={{ background: "var(--text)", color: "var(--bg)" }}
                                >
                                    <DownloadSimple size={16} />
                                    <span className="hidden sm:inline">{bulkBusy ? t("bulk_zipping") : t("bulk_download_zip")}</span>
                                </button>
                            )}
                            <button
                                onClick={bulkDelete}
                                disabled={selected.size === 0 || bulkBusy}
                                data-testid="btn-bulk-delete"
                                className="btn-press h-10 px-3 flex items-center gap-2 text-sm disabled:opacity-40"
                                style={{ background: "var(--danger)", color: "#fff" }}
                            >
                                <Trash size={16} />
                                <span className="hidden sm:inline">{t("gal_delete")}</span>
                            </button>
                            <button
                                onClick={exitSelect}
                                data-testid="btn-bulk-cancel"
                                className="btn-press h-10 px-3 border text-sm"
                                style={{ borderColor: "var(--border)" }}
                            >
                                <X size={14} />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {preview && (
                <PreviewModal
                    item={preview}
                    t={t}
                    onClose={() => setPreview(null)}
                    onDelete={() => removeItem(preview)}
                    onDownload={canDownload ? () => downloadItem(preview) : null}
                    onShare={canDownload ? () => setSharing(preview) : null}
                />
            )}
            {sharing && <ShareModal item={sharing} onClose={() => setSharing(null)} />}
        </div>
    );
}

function TabBtn({ active, onClick, icon: Icon, label, testid }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className="px-4 py-2.5 text-sm btn-press flex items-center gap-2 -mb-px border-b-2"
            style={{
                color: active ? "var(--text)" : "var(--text-muted)",
                borderColor: active ? "var(--text)" : "transparent",
            }}
        >
            <Icon size={16} />
            {label}
        </button>
    );
}

function MediaTile({ item, selectMode, isSelected, onClick, onLongPress, onDelete, onDownload, onShare }) {
    const pressTimer = useRef(null);

    const startPress = () => {
        pressTimer.current = setTimeout(() => {
            onLongPress?.();
            pressTimer.current = null;
        }, 500);
    };
    const endPress = () => {
        if (pressTimer.current) {
            clearTimeout(pressTimer.current);
            pressTimer.current = null;
        }
    };

    return (
        <div
            className="relative group aspect-square overflow-hidden border card-hover"
            style={{
                borderColor: isSelected ? "var(--text)" : "var(--border)",
                background: "var(--surface)",
                outline: isSelected ? "2px solid var(--text)" : "none",
                outlineOffset: "-2px",
            }}
            data-testid={`media-tile-${item.id}`}
        >
            <button
                onClick={onClick}
                onMouseDown={startPress}
                onMouseUp={endPress}
                onMouseLeave={endPress}
                onTouchStart={startPress}
                onTouchEnd={endPress}
                className="absolute inset-0 w-full h-full"
            >
                {item.has_thumb ? (
                    <img
                        src={authedUrl(`/media/${item.id}/thumb`)}
                        alt={item.original_name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        style={{ opacity: isSelected ? 0.55 : 1, transition: "opacity 150ms" }}
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center" style={{ color: "var(--text-muted)" }}>
                        {item.kind === "video" ? <FilmStrip size={36} /> : <ImageIcon size={36} />}
                    </div>
                )}
                {item.kind === "video" && !selectMode && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: "rgba(0,0,0,0.55)" }}>
                            <Play size={22} weight="fill" color="#fff" />
                        </div>
                    </div>
                )}
            </button>

            {selectMode && (
                <div className="absolute top-2 left-2 pointer-events-none" data-testid={`select-mark-${item.id}`}>
                    {isSelected ? (
                        <CheckCircle size={28} weight="fill" color="#fff" style={{ filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.8))" }} />
                    ) : (
                        <Circle size={28} color="#fff" style={{ filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.8))" }} />
                    )}
                </div>
            )}

            {!selectMode && (
                <div className="absolute top-1 right-1 flex gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                    {onShare && (
                        <button
                            onClick={(e) => { e.stopPropagation(); onShare(); }}
                            data-testid={`btn-share-${item.id}`}
                            className="w-8 h-8 flex items-center justify-center btn-press"
                            style={{ background: "rgba(0,0,0,0.7)", color: "var(--text)" }}
                            title="Share"
                        >
                            <ShareNetwork size={14} />
                        </button>
                    )}
                    {onDownload && (
                        <button
                            onClick={(e) => { e.stopPropagation(); onDownload(); }}
                            data-testid={`btn-download-${item.id}`}
                            className="w-8 h-8 flex items-center justify-center btn-press"
                            style={{ background: "rgba(0,0,0,0.7)", color: "var(--text)" }}
                            title="Download"
                        >
                            <DownloadSimple size={14} />
                        </button>
                    )}
                    <button
                        onClick={(e) => { e.stopPropagation(); onDelete(); }}
                        data-testid={`btn-delete-${item.id}`}
                        className="w-8 h-8 flex items-center justify-center btn-press"
                        style={{ background: "rgba(0,0,0,0.7)", color: "var(--danger)" }}
                        title="Delete"
                    >
                        <Trash size={14} />
                    </button>
                </div>
            )}

            <div className="absolute bottom-0 left-0 right-0 px-2 py-1.5 pointer-events-none" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.85), transparent)" }}>
                <div className="text-[11px] font-mono truncate" style={{ color: "var(--text)" }}>{bytesToHuman(item.size)}</div>
            </div>
        </div>
    );
}

function PreviewModal({ item, t, onClose, onDelete, onDownload, onShare }) {
    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-8"
            style={{ background: "rgba(0,0,0,0.92)" }}
            onClick={onClose}
            data-testid="media-preview"
        >
            <button onClick={onClose} className="fixed bottom-24 right-4 w-14 h-14 flex items-center justify-center rounded-full shadow-2xl" style={{ background: "#ef4444", color: "#fff", zIndex: 60 }} data-testid="btn-close-preview" aria-label="Închide">
                <X size={24} weight="bold" />
            </button>
            <div className="relative max-w-5xl w-full max-h-full flex flex-col gap-3" onClick={(e) => e.stopPropagation()}>
                <div className="flex-1 min-h-0 flex items-center justify-center" style={{ maxHeight: "78vh" }}>
                    {item.kind === "image" ? (
                        <img src={authedUrl(`/media/${item.id}/file`)} alt={item.original_name} className="max-w-full max-h-full object-contain" />
                    ) : (
                        <video src={authedUrl(`/media/${item.id}/file`)} controls autoPlay playsInline className="max-w-full max-h-full bg-black" data-testid="preview-video" />
                    )}
                </div>
                <div className="flex items-center justify-between gap-3 border p-3 flex-wrap" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
                    <div className="min-w-0">
                        <div className="text-sm truncate" style={{ color: "var(--text)" }}>{item.original_name}</div>
                        <div className="label-mono">{bytesToHuman(item.size)} · {item.mime}</div>
                    </div>
                    <div className="flex gap-2 shrink-0 flex-wrap">
                        {onShare && (
                            <button onClick={onShare} data-testid="btn-preview-share" className="btn-press h-10 px-3 flex items-center gap-2 border text-sm" style={{ borderColor: "var(--border-strong)" }}>
                                <ShareNetwork size={16} /> {t("gal_share")}
                            </button>
                        )}
                        {onDownload && (
                            <button onClick={onDownload} data-testid="btn-preview-download" className="btn-press h-10 px-3 flex items-center gap-2 border text-sm" style={{ borderColor: "var(--border-strong)" }}>
                                <DownloadSimple size={16} /> {t("gal_download")}
                            </button>
                        )}
                        <button onClick={onDelete} data-testid="btn-preview-delete" className="btn-press h-10 px-3 flex items-center gap-2 text-sm" style={{ background: "var(--danger)", color: "#fff" }}>
                            <Trash size={16} /> {t("gal_delete")}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function EmptyState({ onUpload, label, btnLabel }) {
    return (
        <div className="border border-dashed p-10 sm:p-16 text-center" style={{ borderColor: "var(--border-strong)" }}>
            <div className="label-mono mb-3">empty</div>
            <h3 className="heading-tight text-2xl mb-4">{label}</h3>
            <button
                onClick={onUpload}
                data-testid="btn-empty-upload"
                className="btn-press inline-flex items-center gap-2 px-4 h-11"
                style={{ background: "var(--text)", color: "var(--bg)" }}
            >
                <Plus size={16} /> {btnLabel || "Add"}
            </button>
        </div>
    );
}
